import { useLayoutEffect, useRef, useState } from 'react'

/**
 * Generic hook that measures visible item count for a single-row, no-wrap
 * flex container. When the total chip/tag width exceeds the container, it
 * returns how many items fit alongside the overflow ellipsis indicator.
 *
 * Items must be marked with `data-overflow-item` and the overflow indicator
 * element must be marked with `data-overflow-ellipsis` so the hook can find
 * and measure them.
 *
 * Derived from the TagGroup truncation logic in TagGroup.utils.ts and
 * extracted here so it can be reused by other components (e.g. MultiSelectInput).
 *
 * @param itemCount  Number of items — used to reset cached widths when the
 *                   list changes.
 * @param gap        Gap between items in pixels (should match the CSS gap).
 * @param cacheKey   Optional string that, when changed, invalidates the width
 *                   cache. Pass `items.join('|')` when item labels can change
 *                   without changing the count (e.g. swapping one chip for
 *                   another), so widths are always re-measured from scratch.
 * @returns visibleCount  undefined when all items fit; otherwise the number
 *                        of items that should be visible.
 * @returns refCallback   Attach to the container element.
 */
export function useOverflowCount(
  itemCount: number,
  gap: number = 8,
  cacheKey?: string
): {
  visibleCount: number | undefined
  refCallback: (node: HTMLElement | null) => void
} {
  const [container, setContainer] = useState<HTMLElement | null>(null)
  const itemWidthsRef = useRef<number[]>([])
  const [visibleCount, setVisibleCount] = useState<number | undefined>(
    undefined
  )

  // Reset cached widths and visible count synchronously during render when the
  // item list changes — same pattern as useTagLayout — so all items are
  // visible in the DOM when the layout effect measures them.
  const prevCountRef = useRef(itemCount)
  const prevCacheKeyRef = useRef(cacheKey)
  if (
    prevCountRef.current !== itemCount ||
    prevCacheKeyRef.current !== cacheKey
  ) {
    prevCountRef.current = itemCount
    prevCacheKeyRef.current = cacheKey
    itemWidthsRef.current = []
    if (visibleCount !== undefined) setVisibleCount(undefined)
  }

  useLayoutEffect(() => {
    if (!container) return

    const compute = () => {
      if (!container.isConnected || container.offsetWidth <= 0) return

      // Measure all visible items (hidden ones have offsetWidth 0 — retain
      // the previously cached width for those slots).
      const items = Array.from(
        container.querySelectorAll('[data-overflow-item]')
      ) as HTMLElement[]
      items.forEach((item, i) => {
        if (item.offsetWidth > 0) itemWidthsRef.current[i] = item.offsetWidth
      })
      const widths = itemWidthsRef.current
      if (!widths.length) return

      const ellipsisEl = container.querySelector(
        '[data-overflow-ellipsis]'
      ) as HTMLElement | null
      const ellipsisWidth = ellipsisEl ? ellipsisEl.offsetWidth + gap : 0

      const containerWidth = container.offsetWidth
      const totalWidth = widths.reduce(
        (sum, w, i) => sum + w + (i > 0 ? gap : 0),
        0
      )

      if (totalWidth <= containerWidth) {
        setVisibleCount(undefined)
        return
      }

      let count = 0
      let usedWidth = 0
      for (const w of widths) {
        const g = count > 0 ? gap : 0
        if (usedWidth + g + w + ellipsisWidth <= containerWidth) {
          usedWidth += g + w
          count++
        } else {
          break
        }
      }
      setVisibleCount(count)
    }

    compute()
    if (typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(compute)
    observer.observe(container)
    const ellipsisEl = container.querySelector(
      '[data-overflow-ellipsis]'
    ) as HTMLElement | null
    if (ellipsisEl) observer.observe(ellipsisEl)
    return () => observer.disconnect()
  }, [container, itemCount, gap, cacheKey])

  return { visibleCount, refCallback: setContainer }
}
